import { ProductTypeDto } from "apiClients/useProductTypeApiClient";

export type DropPos = "before" | "after" | "inside";

export const parentKey = (productType: ProductTypeDto): number | null =>
  productType.parentProductTypeId ?? null;

export const compareProductTypes = (a: ProductTypeDto, b: ProductTypeDto) =>
  (a.sortOrder ?? 0) - (b.sortOrder ?? 0) ||
  (a.name || "").localeCompare(b.name || "", "sv", { sensitivity: "base" }) ||
  a.id - b.id;

export const childrenOf = (types: ProductTypeDto[], parentId: number | null) =>
  types.filter((type) => parentKey(type) === parentId).sort(compareProductTypes);

export const descendantIds = (types: ProductTypeDto[], id: number) => {
  const byParent = new Map<number, number[]>();
  types.forEach((type) => {
    if (type.parentProductTypeId == null) return;
    const list = byParent.get(type.parentProductTypeId) ?? [];
    list.push(type.id);
    byParent.set(type.parentProductTypeId, list);
  });

  const out = new Set<number>();
  const stack = [...(byParent.get(id) ?? [])];
  while (stack.length) {
    const current = stack.pop()!;
    if (out.has(current)) continue;
    out.add(current);
    stack.push(...(byParent.get(current) ?? []));
  }
  return out;
};

export const rolledUpProductCount = (types: ProductTypeDto[], id: number) => {
  const byId = new Map(types.map((type) => [type.id, type]));
  let sum = byId.get(id)?.productCount ?? 0;
  descendantIds(types, id).forEach((descendantId) => {
    sum += byId.get(descendantId)?.productCount ?? 0;
  });
  return sum;
};

export const pathOf = (types: ProductTypeDto[], id: number) => {
  const byId = new Map(types.map((type) => [type.id, type]));
  const parts: string[] = [];
  let current = byId.get(id)?.parentProductTypeId ?? undefined;
  const seen = new Set<number>();
  while (current && !seen.has(current)) {
    seen.add(current);
    const node = byId.get(current);
    if (!node) break;
    parts.unshift(node.name || `Typ #${node.id}`);
    current = node.parentProductTypeId ?? undefined;
  }
  return parts.length ? parts.join(" / ") : "Toppnivå";
};

export const siblingIndex = (types: ProductTypeDto[], id: number) => {
  const node = types.find((type) => type.id === id);
  if (!node) return 0;
  const index = childrenOf(types, parentKey(node)).findIndex((type) => type.id === id);
  return index < 0 ? 0 : index;
};

export const moveTarget = (
  types: ProductTypeDto[],
  dragId: number,
  targetId: number | null,
  pos: DropPos
): { parentId: number | null; index: number } | null => {
  if (dragId === targetId) return null;
  if (targetId != null && descendantIds(types, dragId).has(targetId)) return null;

  if (targetId == null) {
    return {
      parentId: null,
      index: childrenOf(types, null).filter((type) => type.id !== dragId).length,
    };
  }

  const target = types.find((type) => type.id === targetId);
  if (!target) return null;

  if (pos === "inside") {
    return {
      parentId: target.id,
      index: childrenOf(types, target.id).filter((type) => type.id !== dragId).length,
    };
  }

  const siblings = childrenOf(types, parentKey(target)).filter((type) => type.id !== dragId);
  const index = siblings.findIndex((type) => type.id === target.id);
  if (index < 0) return null;
  return { parentId: parentKey(target), index: pos === "before" ? index : index + 1 };
};

export const applyMove = (
  types: ProductTypeDto[],
  id: number,
  parentId: number | null,
  index: number
) => {
  const moving = types.find((type) => type.id === id);
  if (!moving) return types;

  const oldParent = parentKey(moving);
  let next = types.map((type) =>
    type.id === id ? { ...type, parentProductTypeId: parentId } : type
  );

  const byId = new Map(next.map((type) => [type.id, type]));
  const levelOf = (nodeId: number, guard = 0): number => {
    const node = byId.get(nodeId);
    if (!node || node.parentProductTypeId == null || guard > types.length) return 1;
    return levelOf(node.parentProductTypeId, guard + 1) + 1;
  };

  const affected = descendantIds(next, id);
  affected.add(id);
  next = next.map((type) => (affected.has(type.id) ? { ...type, level: levelOf(type.id) } : type));

  const place = (
    list: ProductTypeDto[],
    pid: number | null,
    insertId: number | null,
    insertAt: number | null
  ) => {
    const siblings = list
      .filter((type) => parentKey(type) === pid && type.id !== insertId)
      .sort(compareProductTypes);
    if (insertId != null) {
      const node = list.find((type) => type.id === insertId);
      if (node) {
        const at = Math.max(0, Math.min(insertAt ?? siblings.length, siblings.length));
        siblings.splice(at, 0, node);
      }
    }
    const order = new Map(siblings.map((type, siblingIndex) => [type.id, siblingIndex]));
    return list.map((type) => (order.has(type.id) ? { ...type, sortOrder: order.get(type.id)! } : type));
  };

  next = place(next, parentId, id, index);
  if (oldParent !== parentId) next = place(next, oldParent, null, null);
  return next;
};
